/**
 * useAgGridColumns - Hook para definir las columnas de la tabla AG Grid de Pre-Facturas
 *
 * Define las 5 columnas para la tabla de prefacturas:
 * 1. Fecha, 2. Código, 3. Tipo, 4. Total Pre-factura, 5. Facturado
 *
 * @returns {Object} Objeto con columnas de AG Grid y TanStack Table
 */

import { CustomColDef } from '@shared/components/ui/AgGridTable';
import { ColumnDef } from '@tanstack/react-table';
import { ColDef } from 'ag-grid-community';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatCurrencyAmount, useCurrency } from '@modules/Orders/shared/hooks/useCurrency';
import InfoTooltip from '@shared/components/ui/InfoTooltip/InfoTooltip';
import { PreInvoiceDropdownActions } from '../components/interaction/PreInvoiceDropdownActions';
import { useInvoicesPermissions } from '@modules/Invoices/InvoicesOrders/List/hooks/useInvoicesPermissions';

// 🎯 Función utilitaria para formatear fechas como dd/mm/yyyy
const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';

  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '-';

    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  } catch {
    return '-';
  }
};

const convertToTanStackColumns = (agGridColumns: ColDef[], t: (key: string) => string): ColumnDef<any>[] => {
  return agGridColumns
    .filter((col) => {
      const field = col.field || col.headerName || 'unknown';
      return field !== 'actions' && field !== 'unknown';
    })
    .map((col) => {
      let translatedHeader = col.headerName || 'Columna';

      if (translatedHeader.includes('table.')) {
        translatedHeader = t(translatedHeader);
      }

      return {
        id: col.field || col.headerName || 'unknown',
        accessorKey: col.field || col.headerName || 'unknown',
        header: translatedHeader,
      };
    });
};

interface UseAgGridColumnsProps {
  selectedColumns?: string[];
  onViewPreInvoice?: (preInvoiceId: number) => void;
  onInvoicePreInvoice?: (preInvoiceId: number) => void;
  onDeletePreInvoice?: (preInvoiceId: number) => void;
}

export const useAgGridColumns = ({
  selectedColumns = [],
  onViewPreInvoice,
  onInvoicePreInvoice,
  onDeletePreInvoice,
}: UseAgGridColumnsProps) => {
  const { t } = useTranslation('preInvoicesModule');
  const currency = useCurrency(); // 🎯 Obtener moneda del team desde /me
  const { canCreateInvoices } = useInvoicesPermissions();
  const hasPreInvoiceActions = Boolean(onViewPreInvoice) || (Boolean(onInvoicePreInvoice) && canCreateInvoices);

  const allColumnDefs: ColDef[] = useMemo(() => {
    const baseColumns: CustomColDef[] = [
      // 🎯 1. Fecha (Date) - created_at
      {
        field: 'created_at',
        headerName: t('table.created_at', 'Fecha'),
        sortable: false, // ❌ No es ninguno de los campos sortables de la API
        minWidth: 120,
        valueGetter: (params) => params.data?.created_at ?? '',
        cellRenderer: (params: any) => {
          const dateStr = params.data?.created_at || null;
          return <span className="text-sm">{formatDate(dateStr)}</span>;
        },
      },
      // 🎯 2. Código (Code)
      {
        field: 'code',
        headerName: t('table.code', 'Código'),
        sortable: false, // ❌ No es ninguno de los campos sortables de la API
        minWidth: 200,
        valueGetter: (params) => params.data?.code ?? '',
      },
      // 🎯 3. Tipo (Type) - Por defecto "Envío"
      {
        field: 'type',
        headerName: t('table.type', 'Tipo'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => {
          const type = params.data?.type;
          // Mapear tipos de la API a nombres legibles
          if (type === 'SHIPPING') return 'Envío';
          return type ?? 'Envío';
        },
        cellRenderer: (params: any) => {
          const type = params.data?.type;
          // Mapear tipos de la API a nombres legibles
          const displayType = type === 'SHIPPING' ? 'Envío' : (type ?? 'Envío');
          return <span className="text-sm">{displayType}</span>;
        },
      },
      // 🎯 4. Total Pre-factura (Total Pre-invoice) con desglose en tooltip
      {
        field: 'total_amount',
        headerName: t('table.total_amount', 'Total Pre-factura'),
        sortable: false, // ❌ No es ninguno de los campos sortables de la API
        minWidth: 180,
        valueGetter: (params) => params.data?.total_amount ?? 0,
        cellRenderer: (params: any) => {
          const total = params.data?.total_amount ?? 0;
          const variableData = params.data?.variable_data;

          // Formatear el monto total
          const formatWithCurrency = (value: number) => formatCurrencyAmount(value, currency || 'EUR');
          const formattedTotal = formatWithCurrency(total);

          // Construir el texto del tooltip con el desglose (multiline)
          let tooltipText = '';
          if (variableData) {
            const lines: string[] = [];
            if (variableData.SHIPPING !== undefined && variableData.SHIPPING !== null) {
              lines.push(`${t('tooltip.shipping', 'Envío')}: ${formatWithCurrency(variableData.SHIPPING)}`);
            }
            if (variableData.PREPARATION !== undefined && variableData.PREPARATION !== null) {
              lines.push(`${t('tooltip.preparation', 'Preparación')}: ${formatWithCurrency(variableData.PREPARATION)}`);
            }
            if (variableData.COD !== undefined && variableData.COD !== null) {
              lines.push(`${t('tooltip.cod', 'COD')}: ${formatWithCurrency(variableData.COD)}`);
            }

            tooltipText = lines.join('\n');
          }

          return (
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{formattedTotal}</span>
              {tooltipText && (
                <InfoTooltip text={tooltipText} maxWidth="md" position="top" tooltipClassName="whitespace-pre-line" />
              )}
            </div>
          );
        },
      },
      // 🎯 5. Facturado (Invoiced) - status
      {
        field: 'status',
        headerName: t('table.invoiced', 'Facturado'),
        sortable: false,
        minWidth: 120,
        valueGetter: (params) => params.data?.status ?? '',
        cellRenderer: (params: any) => {
          const status = params.data?.status;
          const isClosed = status === 'CLOSED';

          return <span className="text-sm">{isClosed ? t('table.yes', 'Sí') : t('table.no', 'No')}</span>;
        },
      },
    ];

    if (hasPreInvoiceActions) {
      baseColumns.push({
        field: 'actions',
        headerName: '',
        sortable: false,
        filter: false,
        width: 50,
        maxWidth: 50,
        minWidth: 50,
        pinned: 'right',
        lockPosition: 'right',
        suppressMovable: true,
        suppressSizeToFit: true,
        suppressAutoSize: true,
        cellRenderer: (params: any) => {
          const preInvoice = params.data;
          if (!preInvoice || !preInvoice.id) {
            return null;
          }

          // Maneja la acción seleccionada en el menú contextual
          const handleDropdownAction = (action: 'view' | 'invoice' | 'delete', preInvoiceId: number) => {
            if (action === 'view' && onViewPreInvoice) {
              onViewPreInvoice(preInvoiceId);
            } else if (action === 'invoice' && onInvoicePreInvoice) {
              onInvoicePreInvoice(preInvoiceId);
            } else if (action === 'delete' && onDeletePreInvoice) {
              onDeletePreInvoice(preInvoiceId);
            }
          };

          return (
            <div className="flex justify-center w-full">
              <PreInvoiceDropdownActions preInvoice={preInvoice} onAction={handleDropdownAction} />
            </div>
          );
        },
      });
    }

    return baseColumns;
  }, [t, currency, onViewPreInvoice, onInvoicePreInvoice, onDeletePreInvoice, hasPreInvoiceActions]);

  const tanStackColumns = useMemo(() => convertToTanStackColumns(allColumnDefs, t), [allColumnDefs, t]);

  const filteredColumnDefs = useMemo(() => {
    if (!selectedColumns || selectedColumns.length === 0) {
      return allColumnDefs;
    }

    return allColumnDefs.filter((col) => {
      const field = col.field || '';
      if (field === 'actions') {
        return hasPreInvoiceActions;
      }

      return selectedColumns.includes(field);
    });
  }, [allColumnDefs, selectedColumns, hasPreInvoiceActions]);

  return {
    allColumnDefs: filteredColumnDefs,
    filteredColumnDefs,
    tanStackColumns,
  };
};
